import { Fragment, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, PanelLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import AppSidebar from "./AppSidebar";
import { useCurrentOrganization } from "./useCurrentOrganization";

export interface Crumb {
  label: string;
  to?: string;
}

interface Props {
  breadcrumbs: Crumb[];
  children: React.ReactNode;
}

const SIDEBAR_KEY = "brixture.sidebar";
const isDesktop = () => window.matchMedia("(min-width: 768px)").matches;

const initialOpen = () => {
  if (!isDesktop()) return false;
  try {
    return localStorage.getItem(SIDEBAR_KEY) !== "closed";
  } catch {
    return true;
  }
};

const AppShell: React.FC<Props> = ({ breadcrumbs, children }) => {
  const [open, setOpen] = useState(initialOpen);
  const organization = useCurrentOrganization();

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (isDesktop()) {
      try {
        localStorage.setItem(SIDEBAR_KEY, next ? "open" : "closed");
      } catch {
        // Remembering the sidebar is a convenience only.
      }
    }
  };

  const crumbs: Crumb[] = organization.id
    ? [{ label: organization.name ?? "…", to: `/${organization.id}` }, ...breadcrumbs]
    : breadcrumbs;

  return (
    <div className="flex h-full bg-sidebar text-foreground">
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/40 md:hidden"
          aria-hidden="true"
          onClick={() => setOpen(false)}
        />
      )}
      <AppSidebar
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-64 md:static md:z-auto",
          open ? "flex" : "hidden",
        )}
        onNavigate={() => !isDesktop() && setOpen(false)}
      />
      <div className={cn("flex min-w-0 flex-1 flex-col md:py-2 md:pr-2", !open && "md:pl-2")}>
        <main className="flex min-h-0 flex-1 flex-col overflow-hidden bg-background md:rounded-xl md:border md:shadow-xs">
          <header className="flex h-14 shrink-0 items-center gap-2 border-b px-4">
            <Button
              variant="ghost"
              size="icon-sm"
              className="-ml-1"
              onClick={toggle}
              aria-label={open ? "Zijbalk verbergen" : "Zijbalk tonen"}
            >
              <PanelLeft />
            </Button>
            <Separator orientation="vertical" className="mr-2 data-[orientation=vertical]:h-4" />
            <nav aria-label="Kruimelpad" className="min-w-0">
              <ol className="flex items-center gap-1.5 text-sm text-muted-foreground">
                {crumbs.map((crumb, i) => {
                  const last = i === crumbs.length - 1;
                  return (
                    <Fragment key={`${i}-${crumb.label}`}>
                      {i > 0 && <ChevronRight className="size-3.5 shrink-0" aria-hidden="true" />}
                      <li className={cn("truncate", last && "text-foreground")}>
                        {crumb.to && !last ? (
                          <Link to={crumb.to} className="transition-colors hover:text-foreground">
                            {crumb.label}
                          </Link>
                        ) : (
                          <span aria-current={last ? "page" : undefined}>{crumb.label}</span>
                        )}
                      </li>
                    </Fragment>
                  );
                })}
              </ol>
            </nav>
          </header>
          <div className="min-h-0 flex-1 overflow-auto">{children}</div>
        </main>
      </div>
    </div>
  );
};

export default AppShell;
